import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import type { TooltipContentProps } from "recharts";

export interface ChartSlice {
    id: string;
    label: string;
    value: number;
    /** Warna CSS (boleh `var(--...)`) — fallback ke palet bawaan. */
    color?: string;
}

const PALETTE = [
    "var(--color-primary)",
    "var(--color-secondary)",
    "var(--color-tertiary)",
    "#2563eb",
    "#38bdf8",
    "#a855f7",
    "#f43f5e",
];

interface BreakdownDonutChartProps {
    data: ChartSlice[];
    height?: number;
    /** Teks tengah donat, mis. total sesi. */
    centerValue?: string;
    centerLabel?: string;
    formatValue?: (value: number) => string;
}

/**
 * Donat breakdown (distribusi per outlet / olahraga / status).
 * Legend ditulis sendiri (bawaan Recharts kurang cocok dengan tema aplikasi).
 */
export function BreakdownDonutChart({
    data,
    height = 240,
    centerValue,
    centerLabel,
    formatValue = (value) => String(value),
}: BreakdownDonutChartProps) {
    const total = data.reduce((sum, slice) => sum + slice.value, 0);

    return (
        <div className="flex flex-col gap-4">
            <div className="relative" style={{ height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="label"
                            innerRadius="62%"
                            outerRadius="88%"
                            paddingAngle={3}
                            stroke="none"
                            animationDuration={700}
                        >
                            {data.map((slice, index) => (
                                <Cell
                                    key={slice.id}
                                    fill={slice.color ?? PALETTE[index % PALETTE.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip content={(props) => <DonutTooltip props={props} />} />
                    </PieChart>
                </ResponsiveContainer>

                {(centerValue || centerLabel) && (
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        {centerValue && (
                            <span className="app-text-primary text-2xl font-bold">
                                {centerValue}
                            </span>
                        )}
                        {centerLabel && (
                            <span className="app-text-soft text-xs font-medium">
                                {centerLabel}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <ul className="flex flex-col gap-2">
                {data.map((slice, index) => (
                    <li key={slice.id} className="flex items-center gap-2 text-sm">
                        <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{
                                background: slice.color ?? PALETTE[index % PALETTE.length],
                            }}
                        />
                        <span className="app-shell-text-muted min-w-0 flex-1 truncate">
                            {slice.label}
                        </span>
                        <span className="app-text-primary font-semibold">
                            {formatValue(slice.value)}
                        </span>
                        {total > 0 && (
                            <span className="app-text-soft w-10 text-right text-xs">
                                {Math.round((slice.value / total) * 100)}%
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function DonutTooltip({ props }: { props: TooltipContentProps }) {
    const slice = props.payload?.[0]?.payload as ChartSlice | undefined;
    if (!slice) return null;

    return (
        <div className="app-content-card rounded-xl border px-3 py-2 shadow-lg">
            <p className="app-text-primary text-xs font-semibold">{slice.label}</p>
            <p className="app-shell-text-muted mt-0.5 text-xs">{slice.value}</p>
        </div>
    );
}
